import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Select,
  FormControl,
  Typography,
  FormControlLabel,
  Checkbox,
  Chip,
  Paper,
  IconButton,
  TextField,
} from "@mhome/ui";
import AddIcon from "@mui/icons-material/Add";
import { useMediaQuery } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import LabelIcon from "@mui/icons-material/Label";
import SettingsIcon from "@mui/icons-material/Settings";
import { useRecoilValue } from "recoil";
import { safeAreaInsetsState } from "../global-state";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import { spacingToPx } from "../lib/utils";

const UpsertWidgetDialog = ({
  open,
  titleWidget,
  currentWidget,
  existingNames,
  onClose,
  onCreate,
  isUpserting,
}) => {
  const [dupNames, setDupNames] = useState([]);
  const [name, setName] = useState("");
  const [widgetType, setWidgetType] = useState(""); // "data" or "button"
  const [dataType, setDataType] = useState(""); // specific data type for data widgets
  const [unit, setUnit] = useState("");
  const [min, setMin] = useState("");
  const [max, setMax] = useState("");
  const [options, setOptions] = useState([]);
  const [optionInput, setOptionInput] = useState("");
  const [buttonName, setButtonName] = useState("");
  const [isInteger, setIsInteger] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [unitError, setUnitError] = useState(false);
  const [widgetTypeError, setWidgetTypeError] = useState(false);
  const [dataTypeError, setDataTypeError] = useState(false);
  const isSmallScreen = useMediaQuery("@media (max-width:959px)");
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);
  const getComponentBgColor = useComponentBackgroundColor();

  useEffect(() => {
    if (titleWidget) {
      setName(titleWidget.name);
    }
    if (currentWidget) {
      setReadOnly(currentWidget.disabled || false);

      // Determine widget type and data type from current widget
      if (currentWidget.type === "button") {
        setWidgetType("button");
        setButtonName(currentWidget.data?.label || currentWidget.label || "");
      } else {
        setWidgetType("data");
        setDataType(currentWidget.data?.type || currentWidget.dataType || "");
      }

      setUnit(currentWidget.data?.unit || "");
      if (currentWidget.data?.min !== null) {
        setMin(currentWidget.data.min);
      }
      if (currentWidget.data?.max !== null) {
        setMax(currentWidget.data.max);
      }
      setOptions(currentWidget.data?.options ? currentWidget.data.options : []);
      setIsInteger(currentWidget.data?.integer || false);
    }
    return () => {};
  }, [currentWidget]);

  useEffect(() => {
    const names = [...existingNames];
    if (titleWidget) {
      const index = names.indexOf(titleWidget.name);
      if (index > -1) {
        names.splice(index, 1);
      }
    }
    setDupNames(names);
    return () => {};
  }, [titleWidget, existingNames]);

  const handleNameChange = (event) => {
    setName(event.target.value);
  };

  const handleButtonNameChange = (event) => {
    setButtonName(event.target.value);
  };

  const handleWidgetTypeChange = (event) => {
    setWidgetType(event.target.value);
    // Reset data type when widget type changes
    setDataType("");
  };

  const handleDataTypeChange = (event) => {
    setDataType(event.target.value);
  };

  const handleUnitChange = (event) => {
    setUnit(event.target.value);
  };

  const handleMinChange = (event) => {
    setMin(event.target.value);
  };

  const handleMaxChange = (event) => {
    setMax(event.target.value);
  };

  const handleOptionInputChange = (event) => {
    setOptionInput(event.target.value);
  };

  const handleOptionInputKeyDown = (event) => {
    if (event.key === "Enter" && optionInput.trim() !== "") {
      addOption();
    }
  };

  const handleDeleteOption = (optionToDelete) => () => {
    setOptions(options.filter((option) => option !== optionToDelete));
  };

  const addOption = () => {
    if (optionInput.trim() !== "") {
      setOptions([...options, optionInput.trim()]);
      setOptionInput("");
    }
  };

  const handleIsIntegerChange = (event) => {
    setIsInteger(event.target.checked);
  };

  const handleSave = () => {
    if (name.trim() === "") {
      setNameError(true);
      return;
    }
    if (dupNames.indexOf(name.trim()) !== -1) {
      setNameError(true);
      return;
    }
    if (widgetType.trim() === "") {
      setWidgetTypeError(true);
      return;
    }
    if (widgetType === "data" && dataType.trim() === "") {
      setDataTypeError(true);
      return;
    }

    // button widget
    if (widgetType === "button") {
      const dWidget = {
        type: "button",
        disabled: false,
        label: buttonName,
      };
      if (currentWidget) {
        dWidget.id = currentWidget.id;
      }
      onCreate(name, [dWidget]);
      onClose();
      return;
    }

    // data widget
    const dWidget = {
      type: "data",
      dataType: dataType,
      disabled: readOnly,
      repeated: false,
      name,
    };

    if (dataType === "Number") {
      dWidget.min = min;
      dWidget.max = max;
      if (isInteger) {
        dWidget.step = 1;
      }
    } else if (
      dataType === "Duration" ||
      dataType === "Temperature" ||
      dataType === "TemperatureDiff" ||
      dataType === "Weight" ||
      dataType === "Length"
    ) {
      dWidget.min = min;
      dWidget.max = max;
      dWidget.unit = unit;
    } else if (dataType === "Select" || dataType === "MultiSelect") {
      dWidget.dataType = "String";
      dWidget.options = options;
    }

    if (currentWidget) {
      dWidget.id = currentWidget.id;
    }
    onCreate(name, [dWidget]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isSmallScreen}
      variant={isSmallScreen ? undefined : "drawer"}
      PaperProps={{
        elevation: 0,
        style: isSmallScreen
          ? {
              boxShadow: "none",
              borderRadius: 0,
              width: "100%",
              maxWidth: "100%",
              minWidth: "100%",
              marginTop: 0,
              marginBottom: 0,
              height: "100vh",
              maxHeight: "100vh",
              paddingTop: `${safeAreaInsets.top}px`,
              paddingBottom: `${safeAreaInsets.bottom}px`,
              paddingRight: `${safeAreaInsets.right}px`,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }
          : {
              width: "600px",
              maxWidth: "600px",
              minWidth: "600px",
              marginTop: `${safeAreaInsets.top}px !important`,
              marginBottom: `${safeAreaInsets.bottom}px !important`,
              height: `calc(100vh - ${
                safeAreaInsets.top + safeAreaInsets.bottom
              }px) !important`,
              maxHeight: `calc(100vh - ${
                safeAreaInsets.top + safeAreaInsets.bottom
              }px) !important`,
              paddingTop: 0,
              paddingBottom: 0,
              paddingRight: `${safeAreaInsets.right}px`,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },
      }}
    >
      <DialogTitle className="gap-2 flex items-center min-h-0">
        <IconButton aria-label="close" onClick={onClose} className="shrink-0">
          <CloseIcon />
        </IconButton>
        <Typography variant="h6" component="div" className="leading-[1.2] mb-0">
          {currentWidget ? "Update Widget Setting" : "New Widget Card"}
        </Typography>
      </DialogTitle>
      <DialogContent className="px-4 pb-2 flex-1 min-h-0 overflow-auto">
        <div className="flex flex-col gap-4 mt-2">
          {/* Basic Information Card */}
          <Paper
            variant="outlined"
            className="p-4"
            style={{
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
            }}
          >
            <Typography
              variant="subtitle2"
              gutterBottom
              className="flex items-center gap-2 mb-4"
            >
              <LabelIcon fontSize="small" /> Basic Information
            </Typography>
            <div className="flex flex-col gap-4">
              <TextField
                autoFocus
                id="Name"
                type="text"
                fullWidth
                size="small"
                value={name}
                onChange={handleNameChange}
                error={nameError}
              />

              {!currentWidget && (
                <FormControl fullWidth size="small">
                  <Typography
                    variant="body2"
                    className="mb-1"
                    style={{
                      color: "hsl(var(--muted-foreground))",
                    }}
                  >
                    Widget Type
                  </Typography>
                  <Select
                    id="widgetType"
                    value={widgetType}
                    onChange={handleWidgetTypeChange}
                    error={widgetTypeError}
                  >
                    <MenuItem value="data">Data Widget</MenuItem>
                    <MenuItem value="button">Button Widget</MenuItem>
                  </Select>
                </FormControl>
              )}

              {!currentWidget && widgetType === "data" && (
                <FormControl fullWidth size="small">
                  <Typography
                    variant="body2"
                    className="mb-1"
                    style={{
                      color: "hsl(var(--muted-foreground))",
                    }}
                  >
                    Data Type
                  </Typography>
                  <Select
                    id="dataType"
                    value={dataType}
                    onChange={handleDataTypeChange}
                    error={dataTypeError}
                  >
                    <MenuItem value="Bool">Toggle</MenuItem>
                    <MenuItem value="Number">Number</MenuItem>
                    <MenuItem value="Select">Text Select</MenuItem>
                    {/*<MenuItem value="MultiSelect">Multi-Select</MenuItem>*/}
                    <MenuItem value="String">Free-form Text</MenuItem>
                    <MenuItem value="DateGeneral">Date</MenuItem>
                    <MenuItem value="TimeGeneral">Time</MenuItem>
                    <MenuItem value="Duration">Time Duration</MenuItem>
                    <MenuItem value="Temperature">Temperature</MenuItem>
                    <MenuItem value="TemperatureDiff">
                      Temperature Difference
                    </MenuItem>
                    <MenuItem value="Weight">Weight</MenuItem>
                    <MenuItem value="Length">Length</MenuItem>
                    {/*<MenuItem value="MARKDOWN">Markdown</MenuItem>*/}
                    {/*<MenuItem value="COLOR_HEX">Color Picker</MenuItem>*/}
                    {/*<MenuItem value="COLOR_HUE">Color Hue</MenuItem>*/}
                  </Select>
                </FormControl>
              )}
            </div>
          </Paper>

          {/* Widget Configuration Card */}
          {(widgetType === "button" ||
            dataType === "Select" ||
            dataType === "MultiSelect" ||
            dataType === "Number" ||
            dataType === "Duration" ||
            dataType === "Temperature" ||
            dataType === "TemperatureDiff" ||
            dataType === "Weight" ||
            dataType === "Length") && (
            <Paper
              variant="outlined"
              className="p-4"
              style={{
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
              }}
            >
              <Typography
                variant="subtitle2"
                gutterBottom
                className="flex items-center gap-2 mb-4"
              >
                <SettingsIcon fontSize="small" /> Widget Configuration
              </Typography>
              <div className="flex flex-col gap-4">
                {widgetType === "button" && (
                  <TextField
                    id="buttonName"
                    type="text"
                    fullWidth
                    size="small"
                    value={buttonName}
                    onChange={handleButtonNameChange}
                  />
                )}

                {(dataType === "Select" || dataType === "MultiSelect") && (
                  <div>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      className="mb-2 block"
                    >
                      Options
                    </Typography>
                    <Paper
                      elevation={0}
                      component="ul"
                      className="flex flex-wrap list-none p-2 bg-transparent shadow-none"
                    >
                      {options.map((option, index) => (
                        <li key={index} style={{ margin: "4px" }}>
                          <Chip
                            label={option}
                            onDelete={handleDeleteOption(option)}
                            size="small"
                          />
                        </li>
                      ))}
                    </Paper>
                    <div className="flex items-center gap-2">
                      <TextField
                        id="optionInput"
                        label="Add Option"
                        type="text"
                        value={optionInput}
                        onChange={handleOptionInputChange}
                        onKeyDown={handleOptionInputKeyDown}
                        fullWidth
                        size="small"
                      />
                      <IconButton
                        onClick={addOption}
                        color="primary"
                        aria-label="add option"
                      >
                        <AddIcon />
                      </IconButton>
                    </div>
                  </div>
                )}

                {(dataType === "Number" ||
                  dataType === "Duration" ||
                  dataType === "Temperature" ||
                  dataType === "TemperatureDiff" ||
                  dataType === "Weight" ||
                  dataType === "Length") && (
                  <>
                    <TextField
                      id="min"
                      label="Min"
                      type="number"
                      fullWidth
                      size="small"
                      value={min}
                      onChange={handleMinChange}
                    />
                    <TextField
                      id="max"
                      label="Max"
                      type="number"
                      fullWidth
                      size="small"
                      value={max}
                      onChange={handleMaxChange}
                    />
                    {dataType === "Number" && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isInteger}
                            onChange={handleIsIntegerChange}
                            name="integer"
                            color="primary"
                          />
                        }
                        label="Integer"
                      />
                    )}
                    {(dataType === "Duration" ||
                      dataType === "Temperature" ||
                      dataType === "TemperatureDiff" ||
                      dataType === "Weight" ||
                      dataType === "Length") && (
                      <FormControl fullWidth size="small">
                        <Typography
                          variant="body2"
                          className="mb-1"
                          style={{
                            color: "hsl(var(--muted-foreground))",
                          }}
                        >
                          Unit
                        </Typography>
                        <Select
                          id="unit"
                          value={unit}
                          onChange={handleUnitChange}
                          error={unitError}
                        >
                          {dataType === "Duration" && (
                            <MenuItem value="min">min</MenuItem>
                          )}
                          {dataType === "Duration" && (
                            <MenuItem value="sec">sec</MenuItem>
                          )}
                          {dataType === "Duration" && (
                            <MenuItem value="hour">hour</MenuItem>
                          )}
                          {dataType === "Duration" && (
                            <MenuItem value="day">day</MenuItem>
                          )}
                          {dataType === "Duration" && (
                            <MenuItem value="ms">ms</MenuItem>
                          )}
                          {(dataType === "Temperature" ||
                            dataType === "TemperatureDiff") && (
                            <MenuItem value="C">C</MenuItem>
                          )}
                          {(dataType === "Temperature" ||
                            dataType === "TemperatureDiff") && (
                            <MenuItem value="F">F</MenuItem>
                          )}
                          {dataType === "Weight" && (
                            <MenuItem value="kg">kg</MenuItem>
                          )}
                          {dataType === "Weight" && (
                            <MenuItem value="g">g</MenuItem>
                          )}
                          {dataType === "Weight" && (
                            <MenuItem value="mg">mg</MenuItem>
                          )}
                          {dataType === "Weight" && (
                            <MenuItem value="lb">lb</MenuItem>
                          )}
                          {dataType === "Weight" && (
                            <MenuItem value="oz">oz</MenuItem>
                          )}
                          {dataType === "Length" && (
                            <MenuItem value="mm">mm</MenuItem>
                          )}
                          {dataType === "Length" && (
                            <MenuItem value="cm">cm</MenuItem>
                          )}
                          {dataType === "Length" && (
                            <MenuItem value="m">m</MenuItem>
                          )}
                          {dataType === "Length" && (
                            <MenuItem value="km">km</MenuItem>
                          )}
                          {dataType === "Length" && (
                            <MenuItem value="inch">inch</MenuItem>
                          )}
                          {dataType === "Length" && (
                            <MenuItem value="foot">foot</MenuItem>
                          )}
                          {dataType === "Length" && (
                            <MenuItem value="yard">yard</MenuItem>
                          )}
                          {dataType === "Length" && (
                            <MenuItem value="mile">mile</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    )}
                  </>
                )}
              </div>
            </Paper>
          )}
        </div>
      </DialogContent>
      <DialogActions className="px-4 pb-4">
        <Button
          onClick={onClose}
          color="primary"
          disabled={isUpserting}
          variant="outlined"
          className="normal-case"
          style={{ boxShadow: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          disabled={
            isUpserting ||
            !name ||
            !widgetType ||
            (widgetType === "data" && !dataType)
          }
          variant="contained"
          className="normal-case"
          style={{ boxShadow: "none" }}
        >
          {currentWidget ? "Update" : "Add"}
          {isUpserting && <CircularProgress size={20} className="ml-2" />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpsertWidgetDialog;
